"use client";
/**
 * The parts four surfaces share, so one edible thing is not described four
 * ways.
 *
 * The energy figure is the load-bearing piece. It is never rendered without a
 * decision about its basis, because the captured food rows are measured `Per
 * 101g`, `Per 100g` and `Per 4 oz` and 197 beside 110 beside 90 is not a
 * comparison until the reader knows that.
 */
import type { ReactElement } from "react";
import {
  CresultcardChipRow,
  CresultcardFactLine,
  CresultcardFocal,
  CresultcardMedia,
  CresultcardMediaKind,
  CresultcardStatRow,
  CresultcardSubline,
  CresultcardAlign,
  CresultcardChipRegister,
  CresultcardChipVariant,
  CresultcardFigureScale,
  CresultcardMediaScale,
  CresultcardSublineSize,
  type CresultcardChipSpec,
  type CresultcardFactLineEntry,
  type CresultcardStatSpec,
} from "@/components/ui/Cresultcard";
import {
  CNUTRITIONCARD_ENERGY_LABEL,
  CNUTRITIONCARD_ENERGY_UNIT,
  CNUTRITIONCARD_SEE_ALL_CATEGORIES_LABEL,
  CNUTRITIONCARD_SERVINGS_NOUN,
  CNUTRITIONCARD_SERVING_NOUN,
  CNUTRITIONCARD_YIELD_SUFFIX,
  C_NUTRITION_CHIP_LIMIT,
  C_NUTRITION_KIND_LABEL,
  C_NUTRITION_ROW_DROP_ORDER,
} from "./cnutritioncard.constants";
import {
  cnutritioncardEnergy,
  cnutritioncardLeadServing,
  cnutritioncardMacros,
  cnutritioncardOrigin,
  cnutritioncardServingBasis,
  cnutritioncardTimeLine,
} from "./cnutritioncard.model";
import {
  CnutritioncardMetaSlot,
  CnutritioncardServingScope,
  type CnutritioncardData,
} from "./cnutritioncard.types";

interface CnutritioncardMetaProps {
  readonly data: CnutritioncardData;
}

/**
 * The row's second line: what kind of thing this is, whose it is, and what its
 * figure is measured against.
 *
 * The serving never drops. It is the fact the energy figure beside it is
 * meaningless without, and it is the one field that genuinely differs across
 * the captured rows.
 */
export function CnutritioncardMetaLine({
  data,
}: CnutritioncardMetaProps): ReactElement {
  const origin = cnutritioncardOrigin(data);
  const basis = cnutritioncardServingBasis(cnutritioncardLeadServing(data));
  const time = cnutritioncardTimeLine(data);
  const facts: readonly CresultcardFactLineEntry[] = [
    ...(basis == null
      ? []
      : [{ key: CnutritioncardMetaSlot.Serving, value: basis }]),
    ...(origin == null
      ? []
      : [{ key: CnutritioncardMetaSlot.Brand, value: origin }]),
    {
      key: CnutritioncardMetaSlot.Kind,
      value: C_NUTRITION_KIND_LABEL[data.kind],
    },
    ...(data.numberOfServings == null
      ? []
      : [
          {
            key: CnutritioncardMetaSlot.Yield,
            value: `${String(data.numberOfServings)} ${CNUTRITIONCARD_YIELD_SUFFIX}`,
          },
        ]),
    ...(time == null
      ? []
      : [{ key: CnutritioncardMetaSlot.Time, value: time }]),
  ];
  return (
    <CresultcardFactLine
      dropOrder={C_NUTRITION_ROW_DROP_ORDER}
      facts={facts}
    />
  );
}

interface CnutritioncardEnergyProps extends CnutritioncardMetaProps {
  readonly scale: CresultcardFigureScale;
  readonly servingScope: CnutritioncardServingScope;
  readonly align?: CresultcardAlign;
}

/**
 * The headline energy figure, with or without the basis it is measured
 * against.
 *
 * Under `Frame only` the support line goes and the set's own context line
 * carries the basis for everything beneath it — which is honest exactly as long
 * as every row in the set shares one, and is the reason the axis exists rather
 * than being decided here.
 */
export function CnutritioncardEnergy({
  data,
  scale,
  servingScope,
  align = CresultcardAlign.End,
}: CnutritioncardEnergyProps): ReactElement | null {
  const serving = cnutritioncardLeadServing(data);
  const energy = cnutritioncardEnergy(serving);
  if (energy == null) {
    return null;
  }
  const basis = cnutritioncardServingBasis(serving);
  return (
    <CresultcardFocal
      align={align}
      scale={scale}
      support={
        servingScope === CnutritioncardServingScope.Stated ? basis : undefined
      }
      title={CNUTRITIONCARD_ENERGY_LABEL}
      unit={CNUTRITIONCARD_ENERGY_UNIT}
      value={energy}
    />
  );
}

/**
 * The three macros as one labelled strip.
 *
 * A measured `0` — every captured chicken serving's carbohydrate — is a real
 * reading and stays. Only an absent measurement is dropped, so a food that was
 * never analysed for fat does not read as a fat-free food.
 */
export function CnutritioncardMacroStrip({
  data,
}: CnutritioncardMetaProps): ReactElement | null {
  const serving = cnutritioncardLeadServing(data);
  if (serving == null) {
    return null;
  }
  const macros = cnutritioncardMacros(serving.nutrients);
  if (macros.length === 0) {
    return null;
  }
  const stats: readonly CresultcardStatSpec[] = macros.map((macro) => ({
    label: macro.label,
    value: `${String(macro.value)} ${macro.unit}`,
  }));
  return (
    <CresultcardStatRow
      align={CresultcardAlign.Start}
      stats={stats}
    />
  );
}

/**
 * The recipe's own types and categories.
 *
 * The types lead and take the accent, because the captured record carries three
 * of them against ten categories — and the ten include both `Dessert` and
 * `Cookie Dessert`, which is the vendor's own taxonomy nesting rather than ten
 * independent facts.
 */
export function CnutritioncardCategoryChips({
  data,
}: CnutritioncardMetaProps): ReactElement | null {
  const chips: readonly CresultcardChipSpec[] = [
    ...data.types.map((type) => ({
      key: type,
      label: type,
      variant: CresultcardChipVariant.Accent,
    })),
    ...data.categories.map((category) => ({
      key: category,
      label: category,
    })),
  ];
  if (chips.length === 0) {
    return null;
  }
  return (
    <CresultcardChipRow
      chips={chips}
      emphasiseFirst
      limit={C_NUTRITION_CHIP_LIMIT}
      register={CresultcardChipRegister.Attribute}
      seeAllLabel={CNUTRITIONCARD_SEE_ALL_CATEGORIES_LABEL}
      wrap
    />
  );
}

interface CnutritioncardShotProps extends CnutritioncardMetaProps {
  readonly scale: CresultcardMediaScale;
  readonly onOpen?: () => void;
}

/**
 * The dish photograph, or the designed absence half this pillar renders.
 *
 * `Photograph` rather than `Still` or `Product`: a plated dish is a scene, the
 * background is part of it, and cropping it into a fixed shape is the ordinary
 * presentation rather than a loss. The frame is capped at the measured 300-pixel
 * long edge everywhere it is used, so `cover` never has to upscale the proxy.
 *
 * The fallback's secondary line names the KIND rather than apologising: a food
 * has no picture because the food endpoint publishes none, which is a fact
 * about the endpoint and not a failure of this row.
 */
export function CnutritioncardShot({
  data,
  scale,
  onOpen,
}: CnutritioncardShotProps): ReactElement {
  const [image] = data.images;
  return (
    <CresultcardMedia
      alt={data.name}
      fallbackSecondary={C_NUTRITION_KIND_LABEL[data.kind]}
      fallbackTitle={data.name}
      intrinsicHeight={image?.intrinsicHeight}
      intrinsicWidth={image?.intrinsicWidth}
      kind={CresultcardMediaKind.Photograph}
      onOpen={image == null ? undefined : onOpen}
      scale={scale}
      src={image?.src}
    />
  );
}

/** How many alternative servings the opened pane holds, as one short line. */
export function CnutritioncardServingCount({
  count,
}: {
  readonly count: number;
}): ReactElement | null {
  if (count === 0) {
    return null;
  }
  return (
    <CresultcardSubline
      size={CresultcardSublineSize.Small}
    >
      {`${String(count)} more ${count === 1 ? CNUTRITIONCARD_SERVING_NOUN : CNUTRITIONCARD_SERVINGS_NOUN}`}
    </CresultcardSubline>
  );
}
